import { FC, FormEvent, useState } from 'react'
import { PaymentMethod } from '@stripe/stripe-js/types/api/payment-methods'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { message, Skeleton } from 'antd'

import Box from '~/core/Box'
import Button from '~/core/Button'
import { AllowedStripePaymentTypes } from '~/core/StripeElements/AllowedStripePaymentTypes'

const PLACEMENT = 'new-payment-form-modal'

interface NewPaymentFormProps {
    isLocatedInCheckoutPage?: boolean
    onPaymentAdded: (setupIntent?: PaymentMethod) => Promise<any>
    type: AllowedStripePaymentTypes
}

export const NewPaymentForm: FC<NewPaymentFormProps> = ({
    isLocatedInCheckoutPage,
    onPaymentAdded,
    type,
}) => {
    const [loadingElements, setLoadingElements] = useState(true)
    const [loadingSubmit, setLoadingSubmit] = useState(false)
    const stripe = useStripe()
    const elements = useElements()

    let paymentText = 'payment option'
    let paymentKeyword = 'payment'

    switch (type) {
        case 'card':
            paymentText = 'credit card'
            paymentKeyword = 'card'
            break
        case 'us_bank_account':
            paymentText = 'bank account'
            paymentKeyword = 'ach'
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (!elements || !stripe) {
            return
        }
        setLoadingSubmit(true)
        const { error: submitError } = await elements.submit()

        if (submitError) {
            message.error(submitError.message)
            setLoadingSubmit(false)
            return
        }

        const { error: setupError, setupIntent } = await stripe.confirmSetup({
            elements: elements || undefined,
            redirect: 'if_required',
            confirmParams: {
                return_url: window.location.href,
                expand: ['payment_method'],
            },
        })

        if (setupError) {
            message.error(setupError.message)
        }

        message.success(`${paymentText} was successfully added!`)
        setLoadingSubmit(false)

        await onPaymentAdded(setupIntent?.payment_method as PaymentMethod)
    }

    return (
        <form onSubmit={handleSubmit}>
            {(loadingElements || !elements || !stripe) && <Skeleton active />}
            <PaymentElement
                options={{ layout: 'tabs' }}
                onLoaderStart={() => setLoadingElements(false)}
            />
            <Box data-test-target="payment-form-footer">
                <Button
                    type="primary"
                    htmlType="submit"
                    style={{ marginLeft: 'auto' }}
                    name={`${PLACEMENT}-add-new-${paymentKeyword}`}
                    placement={PLACEMENT}
                    data-test-target={`${PLACEMENT}-add-new-${paymentKeyword}`}
                    disabled={loadingSubmit}
                    loading={loadingSubmit}
                >
                    {isLocatedInCheckoutPage
                        ? `Use this ${paymentText}`
                        : `Add new ${paymentText}`}
                </Button>
            </Box>
        </form>
    )
}
